import type { Cell, BoardConfig, Pos } from "./types";
import { bossCell, isBoss, isSpecial, isWild } from "./types";

/**
 * Pure grid helpers. The grid is a flat array of length rows*cols,
 * indexed `r * cols + c`. `0` = empty. Blocked cells are tracked separately
 * and are always kept at `0`.
 */

export interface Grid {
  cols: number;
  rows: number;
  cells: Cell[];
  /** Blocked-cell mask, same indexing as `cells`. */
  blocked: boolean[];
}

export function createGrid(config: BoardConfig): Grid {
  const { cols, rows } = config;
  const blocked = new Array<boolean>(cols * rows).fill(false);
  for (const [r, c] of config.blockedCells ?? []) {
    blocked[r * cols + c] = true;
  }
  const cells = new Array<Cell>(cols * rows).fill(0);
  // Boss cells sit on the board from the start (fixed; broken by merges).
  for (const [r, c, hp] of config.bosses ?? []) {
    cells[r * cols + c] = bossCell(hp);
  }
  // Pre-filled tiers (puzzle mode). Authors are responsible for placing them
  // gravity-stable; we don't auto-settle so layouts render exactly as designed.
  for (const [r, c, tier] of config.presetCells ?? []) {
    cells[r * cols + c] = tier;
  }
  return { cols, rows, cells, blocked };
}

export const idx = (g: Grid, r: number, c: number): number => r * g.cols + c;

export const at = (g: Grid, r: number, c: number): Cell => g.cells[idx(g, r, c)];

export const isBlocked = (g: Grid, r: number, c: number): boolean =>
  g.blocked[idx(g, r, c)];

export function inBounds(g: Grid, r: number, c: number): boolean {
  return r >= 0 && r < g.rows && c >= 0 && c < g.cols;
}

export function cloneGrid(g: Grid): Grid {
  return { cols: g.cols, rows: g.rows, cells: g.cells.slice(), blocked: g.blocked.slice() };
}

/**
 * Landing row for a piece dropped down column `c`: the lowest empty,
 * non-blocked cell reachable from the top (a piece stops at the first
 * blocked or filled cell). Returns -1 if the column cannot accept a piece.
 */
export function landingRow(g: Grid, c: number): number {
  let landing = -1;
  for (let r = 0; r < g.rows; r++) {
    const i = idx(g, r, c);
    if (g.blocked[i] || g.cells[i] !== 0) break;
    landing = r;
  }
  return landing;
}

/** Columns that can currently accept a drop. */
export function droppableColumns(g: Grid): number[] {
  const out: number[] = [];
  for (let c = 0; c < g.cols; c++) {
    if (landingRow(g, c) >= 0) out.push(c);
  }
  return out;
}

const NEIGHBORS: ReadonlyArray<readonly [number, number]> = [
  [-1, 0],
  [1, 0],
  [0, -1],
  [0, 1],
];

/**
 * Orthogonally-connected component anchored at the seed cell.
 *
 * If the seed is a tier T, the component contains cells of tier T plus any
 * wilds reachable through them (wilds act as universal joiners, spec §3.6).
 * If the seed is wild or empty, the lone-seed group is returned — wilds need
 * a tier anchor to participate in a merge, which the engine resolves
 * separately for wild triggers.
 */
export function connectedGroup(g: Grid, seed: Pos): Pos[] {
  const value = at(g, seed.r, seed.c);
  if (value === 0 || isSpecial(value)) return [seed];
  return connectedTierGroup(g, seed, value);
}

/**
 * BFS for the connected blob of {tier OR wild} cells reachable from `seed`.
 * Used by both the trigger path and the whole-board scan so wilds always join
 * the right anchor tier.
 */
export function connectedTierGroup(g: Grid, seed: Pos, tier: number): Pos[] {
  const startIdx = idx(g, seed.r, seed.c);
  const seedVal = g.cells[startIdx];
  if (seedVal !== tier && !isWild(seedVal)) return [];
  const seen = new Set<number>([startIdx]);
  const stack: Pos[] = [seed];
  const group: Pos[] = [];
  while (stack.length) {
    const cur = stack.pop()!;
    group.push(cur);
    for (const [dr, dc] of NEIGHBORS) {
      const nr = cur.r + dr;
      const nc = cur.c + dc;
      if (!inBounds(g, nr, nc)) continue;
      const ni = idx(g, nr, nc);
      if (seen.has(ni)) continue;
      const nv = g.cells[ni];
      if (nv === tier || isWild(nv)) {
        seen.add(ni);
        stack.push({ r: nr, c: nc });
      }
    }
  }
  return group;
}

/**
 * All maximal same-tier components with size >= `mergeMin`, excluding cells at
 * the tier cap (which cannot promote). Wilds are folded into whichever tier's
 * component reaches them first (deterministic by row-major iteration). Each
 * cell appears in at most one group; leftover wilds stay on the board.
 */
export function allMergeableGroups(g: Grid, maxTier: number, mergeMin: number): Pos[][] {
  const seen = new Array<boolean>(g.cells.length).fill(false);
  const groups: Pos[][] = [];
  for (let r = 0; r < g.rows; r++) {
    for (let c = 0; c < g.cols; c++) {
      const i = idx(g, r, c);
      const v = g.cells[i];
      // Anchor only on real tiers — wilds need an anchor and can't merge alone.
      if (v <= 0 || v >= maxTier || seen[i] || isSpecial(v)) continue;
      const group = connectedTierGroup(g, { r, c }, v);
      // Only mark cells of this tier as "seen" globally; wilds stay claimable
      // by the group that first walked through them, but the seen-mask must
      // also include them so we don't re-visit them in a later iteration.
      for (const p of group) seen[idx(g, p.r, p.c)] = true;
      if (group.length >= mergeMin) groups.push(group);
    }
  }
  return groups;
}

/**
 * Resolve cell where a promoted group lands: lowest row wins; on a tie the
 * trigger cell is used if it sits at that row, otherwise the leftmost cell
 * (spec §3.3 — deterministic, no ambiguity).
 */
export function resultCell(group: Pos[], trigger?: Pos): Pos {
  let maxR = -1;
  for (const p of group) if (p.r > maxR) maxR = p.r;
  if (trigger && trigger.r === maxR && group.some((p) => p.r === trigger.r && p.c === trigger.c)) {
    return trigger;
  }
  let best = group[0];
  for (const p of group) {
    if (p.r > best.r || (p.r === best.r && p.c < best.c)) best = p;
  }
  return best;
}

/**
 * Compact every column downward. Blocked cells AND boss cells are solid
 * dividers: bosses are anchored in place and pieces stack on top of them.
 */
export function applyGravity(g: Grid): void {
  for (let c = 0; c < g.cols; c++) {
    let writeR = g.rows - 1;
    for (let r = g.rows - 1; r >= 0; r--) {
      const i = idx(g, r, c);
      if (g.blocked[i] || isBoss(g.cells[i])) {
        writeR = r - 1;
        continue;
      }
      if (g.cells[i] !== 0) {
        if (writeR !== r) {
          g.cells[idx(g, writeR, c)] = g.cells[i];
          g.cells[i] = 0;
        }
        writeR -= 1;
      }
    }
  }
}
